import React, { createContext, useContext, useReducer, useEffect, type Dispatch } from "react";
import { AppState, AppAction } from "../types/pokemon";
import { appReducer, INITIAL_STATE } from "./appReducer";

const AppContext = createContext<{
  state: AppState;
  dispatch: Dispatch<AppAction>;
} | null>(null);

const STORAGE_KEYS = {
  teams: "pokemon-teams",
  settings: "pokemon-settings",
  audio: "pokemon-audio",
  savedTeams: "pokemon-saved-teams",
} as const;

function loadPersistedState(): Partial<AppState> {
  try {
    const teams = localStorage.getItem(STORAGE_KEYS.teams);
    const settings = localStorage.getItem(STORAGE_KEYS.settings);
    const audio = localStorage.getItem(STORAGE_KEYS.audio);
    const savedTeams = localStorage.getItem(STORAGE_KEYS.savedTeams);
    return {
      ...(teams ? { teams: JSON.parse(teams) } : {}),
      ...(settings ? { settings: { ...INITIAL_STATE.settings, ...JSON.parse(settings) } } : {}),
      ...(audio ? { audio: JSON.parse(audio) } : {}),
      ...(savedTeams ? { savedTeams: JSON.parse(savedTeams) } : {}),
    };
  } catch {
    return {};
  }
}

export function AppProvider({ children }: { children: React.ReactNode }) {
  const persisted = loadPersistedState();
  const [state, dispatch] = useReducer(appReducer, { ...INITIAL_STATE, ...persisted });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.teams, JSON.stringify(state.teams));
      localStorage.setItem(STORAGE_KEYS.settings, JSON.stringify(state.settings));
      localStorage.setItem(STORAGE_KEYS.audio, JSON.stringify(state.audio));
      localStorage.setItem(STORAGE_KEYS.savedTeams, JSON.stringify(state.savedTeams));
    } catch { /* localStorage full or disabled */ }
  }, [state.teams, state.settings, state.audio, state.savedTeams]);

  return (
    <AppContext.Provider value={{ state, dispatch }}>
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used within AppProvider");
  return ctx;
}
